import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import DateInput from "../DateInput";
import { Plus, Trash2, Save, CalendarRange } from "lucide-react";

/**
 * Seasons management section embedded in Admin > Business Settings.
 *
 * Enforces the V1.0.1 spec on both the client and server:
 *   - Season names are dynamic (free-text, no predefined list)
 *   - Unlimited seasons per set
 *   - Start ≤ End
 *   - No overlapping ranges
 *   - No gaps (season B must start exactly on day after season A ends)
 *
 * The exact same rules run on the backend (`pricing.validate_season_set`),
 * so the client-side check is a UX guard, not a security boundary.
 */
export default function SeasonsSection() {
  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/seasons")
      .then((r) => {
        if (cancelled) return;
        const sorted = [...r.data].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        setRows(sorted.map((s) => ({ id: s.id, name: s.name, start_date: s.start_date, end_date: s.end_date })));
        setInitialRows(sorted);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const dirty =
    rows.length !== initialRows.length ||
    rows.some((r, i) => {
      const src = initialRows[i];
      return !src || src.name !== r.name || src.start_date !== r.start_date || src.end_date !== r.end_date;
    });

  const addRow = () => {
    const last = rows[rows.length - 1];
    const nextStart = last?.end_date ? nextDayIso(last.end_date) : "";
    setRows([...rows, { name: "", start_date: nextStart, end_date: "" }]);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const clientValidate = (list) => {
    // Empty is allowed (turns off seasonal pricing).
    if (list.length === 0) return null;
    const clean = list.map((r, i) => {
      const name = (r.name || "").trim();
      if (!name) return `Row ${i + 1}: name is required.`;
      if (!r.start_date || !r.end_date) return `Season "${name}": both start and end dates are required.`;
      if (r.start_date > r.end_date) return `Season "${name}": start date is after end date.`;
      return { name, start_date: r.start_date, end_date: r.end_date };
    });
    const err = clean.find((c) => typeof c === "string");
    if (err) return err;

    const seenNames = new Set();
    for (const c of clean) {
      if (seenNames.has(c.name.toLowerCase())) return `Duplicate season name "${c.name}". Each season must have a unique name.`;
      seenNames.add(c.name.toLowerCase());
    }

    const sorted = [...clean].sort((a, b) => a.start_date.localeCompare(b.start_date));
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      if (cur.start_date <= prev.end_date) {
        return `Overlap: season "${cur.name}" starts on ${cur.start_date} but "${prev.name}" ends on ${prev.end_date}. "${cur.name}" must start on ${nextDayIso(prev.end_date)} or later.`;
      }
      const expected = nextDayIso(prev.end_date);
      if (cur.start_date !== expected) {
        return `Gap: "${prev.name}" ends on ${prev.end_date}; "${cur.name}" must start on ${expected} (currently ${cur.start_date}). The season timeline must be continuous.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const clientErr = clientValidate(rows);
    if (clientErr) {
      toast.warning("Cannot save seasons", { description: clientErr, duration: 8000 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        seasons: rows.map((r) => ({
          name: r.name.trim(), start_date: r.start_date, end_date: r.end_date,
        })),
      };
      const { data } = await api.put("/admin/seasons/bulk", payload);
      const sorted = [...data].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
      setRows(sorted.map((s) => ({ id: s.id, name: s.name, start_date: s.start_date, end_date: s.end_date })));
      setInitialRows(sorted);
      toast.success("Seasons saved", { description: `${sorted.length} season${sorted.length === 1 ? "" : "s"} configured for pricing.` });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error("Save failed", { description: msg, duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-6" data-testid="seasons-section">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-display text-xl">Seasons</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-lg">
            Free-text season names. The full timeline must be continuous — no overlaps, no gaps. Rates are configured per Car Group on the Fleet page.
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white rounded"
          data-testid="add-season"
        >
          <Plus size={13} /> Add season
        </button>
      </div>

      {loading && <p className="text-sm text-neutral-500">Loading seasons…</p>}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-neutral-500 dark:text-neutral-400 italic py-6 border border-dashed border-black/10 dark:border-white/10 rounded flex items-center justify-center gap-2">
          <CalendarRange size={14} /> No seasons configured — Car Groups will use their standard daily rate.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-2" data-testid="seasons-list">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-[10px] tracking-[0.2em] uppercase text-neutral-500 dark:text-neutral-400 px-1">
            <span>Season name</span>
            <span>Start</span>
            <span>End</span>
            <span></span>
          </div>
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center" data-testid={`season-row-${idx}`}>
              <input
                value={row.name}
                onChange={(e) => updateRow(idx, { name: e.target.value })}
                placeholder="e.g. Easter Peak"
                className="editorial-input"
                data-testid={`season-name-${idx}`}
              />
              <DateInput
                value={row.start_date || ""}
                onChange={(v) => updateRow(idx, { start_date: v })}
                inputSize="sm"
                testId={`season-start-${idx}`}
              />
              <DateInput
                value={row.end_date || ""}
                onChange={(v) => updateRow(idx, { end_date: v })}
                inputSize="sm"
                testId={`season-end-${idx}`}
              />
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded"
                aria-label="Remove season"
                data-testid={`season-remove-${idx}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-5">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black text-sm px-5 py-2 rounded disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="save-seasons"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save seasons"}
        </button>
      </div>
    </div>
  );
}

function nextDayIso(iso) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
